const APP_EXPERIENCE_MODE_STORAGE_KEY = 'wp_app_experience_mode';
const AUTH_REDIRECT_LOCK_KEY = 'wp_auth_redirect_lock';
const AUTH_REDIRECT_LOCK_MS = 2500;

type AppExperienceMode = 'player' | 'court-intelligence';

function normalizeAppExperienceMode(value: string | null | undefined): AppExperienceMode | null {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'player') return 'player';
  if (['court-intelligence', 'court_intelligence', 'courtintel', 'court-intel'].includes(normalized)) {
    return 'court-intelligence';
  }
  return null;
}

function readStoredExperienceMode(): AppExperienceMode | null {
  if (typeof window === 'undefined') return null;
  try {
    return normalizeAppExperienceMode(window.localStorage.getItem(APP_EXPERIENCE_MODE_STORAGE_KEY));
  } catch {
    return null;
  }
}

function canRedirectNow() {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.sessionStorage.getItem(AUTH_REDIRECT_LOCK_KEY);
    const last = raw ? Number(raw) : 0;
    const now = Date.now();
    if (Number.isFinite(last) && now - last < AUTH_REDIRECT_LOCK_MS) {
      return false;
    }
    window.sessionStorage.setItem(AUTH_REDIRECT_LOCK_KEY, String(now));
    return true;
  } catch {
    return true;
  }
}

export function resolveUnauthorizedReturnTo(): string {
  if (typeof window === 'undefined') return '/main-app';

  const { pathname, search, hash } = window.location;
  const currentPath = `${pathname}${search}${hash}`;
  const mode = readStoredExperienceMode();

  if (pathname.startsWith('/main-app/court-intelligence')) {
    return currentPath;
  }
  if (mode === 'court-intelligence') {
    return '/main-app/court-intelligence/venues';
  }
  if (pathname.startsWith('/main-app')) {
    return currentPath;
  }
  return '/main-app';
}

export function redirectToLoginForUnauthorized(returnTo?: string) {
  if (typeof window === 'undefined') return false;
  if (window.location.pathname.startsWith('/auth/')) return false;
  if (!canRedirectNow()) return false;

  const targetReturnTo = returnTo && returnTo.startsWith('/') ? returnTo : resolveUnauthorizedReturnTo();
  const target = `/auth/login?returnTo=${encodeURIComponent(targetReturnTo)}`;
  window.location.assign(target);
  return true;
}

function extractRequestPath(input: RequestInfo | URL): string | null {
  if (typeof window === 'undefined') return null;

  const resolveUrl = (value: string) => {
    try {
      return new URL(value, window.location.origin);
    } catch {
      return null;
    }
  };

  if (typeof input === 'string') {
    return resolveUrl(input)?.pathname ?? null;
  }
  if (input instanceof URL) {
    return resolveUrl(input.toString())?.pathname ?? null;
  }
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return resolveUrl(input.url)?.pathname ?? null;
  }
  return null;
}

export function shouldRedirectForUnauthorizedApiCall(input: RequestInfo | URL, response: Response) {
  if (response.status !== 401) return false;
  const pathname = extractRequestPath(input);
  if (!pathname) return false;
  return pathname.startsWith('/api/');
}

